// Thin wrapper around window.oc_trigger (provided by ifl-user-logs plugin).
// All broadcast events MUST go through this so the payload shape stays consistent.

const getPageContext = () => {
  const d = window.broadcastPlayerData || {};
  return {
    post_id: d.postId || 0,
    post_title: d.postTitle || '',
    source_page_type: d.sourcePageType || '',
    is_logged_in: !!d.isLoggedIn,
    language: d.language || 'en-US',
  };
};

const fire = (slug, data = {}) => {
  if (typeof window.oc_trigger !== 'function') {
    if (window.broadcastPlayerData?.debug) {
      console.warn('[playerAnalytics] oc_trigger not ready; dropping', slug);
    }
    return;
  }
  const payload = {
    ...getPageContext(),
    ts_client: Date.now(),
    ...data,
  };
  window.oc_trigger(slug, { event_data: JSON.stringify(payload) });
  if (window.broadcastPlayerData?.debug) {
    console.log('[playerAnalytics]', slug, payload);
  }
};

// ---------- Media events ----------
export const trackPlay     = (ep)           => fire('broadcast_play',     episodeFields(ep));
export const trackPause    = (ep, pos)      => fire('broadcast_pause',    { ...episodeFields(ep), position_seconds: pos });
export const trackResume   = (ep, pos)      => fire('broadcast_resume',   { ...episodeFields(ep), position_seconds: pos });
export const trackSeek     = (ep, from, to) => fire('broadcast_seek',     { ...episodeFields(ep), from_seconds: from, to_seconds: to });
export const trackNext     = (from, to)     => fire('broadcast_next',     { from_episode_id: episodeId(from), to_episode_id: episodeId(to) });
export const trackPrev     = (from, to)     => fire('broadcast_prev',     { from_episode_id: episodeId(from), to_episode_id: episodeId(to) });
export const trackComplete = (ep, watched)  => fire('broadcast_complete', { ...episodeFields(ep), duration_watched: watched });

// Called once per 25/50/75 threshold crossing (don't fire every timeupdate)
export const trackProgress = (ep, milestone, pos) =>
  fire(`broadcast_progress_${milestone}`, { ...episodeFields(ep), position_seconds: pos });

// ---------- Ad events (Google IMA SDK) ----------
export const trackAdImpression = (ad)     => fire('broadcast_ad_impression', adFields(ad));
export const trackAdClick      = (ad)     => fire('broadcast_ad_click',      adFields(ad));
export const trackAdComplete   = (ad)     => fire('broadcast_ad_complete',   adFields(ad));
export const trackAdSkip       = (ad, at) => fire('broadcast_ad_skip',       { ...adFields(ad), skip_at_seconds: at });

// ---------- Shape helpers ----------
// Resolve an episode identifier across the spec's canonical shape and the
// playlist-item shape built by BroadcastPlayer.jsx / SeriesPlayer.jsx.
const episodeId = (ep) =>
  ep?.id || ep?.guid || ep?.['ifl:guid'] || ep?.['ifl:episodeID'] || '';

// Reads the spec's canonical field names first, then falls back to the
// playlist-item shape emitted by BroadcastPlayer.jsx / SeriesPlayer.jsx
// (`guid`, `name`, `series`, `seriesName`, `duration`), then to raw RSS keys.
const episodeFields = (ep) => ({
  episode_id:          episodeId(ep),
  episode_title:       ep?.title || ep?.name || ep?.['ifl:title'] || '',
  series_id:           ep?.seriesId || ep?.series || ep?.['ifl:seriesURL'] || '',
  series_title:        ep?.seriesTitle || ep?.seriesName || ep?.['ifl:seriesTitle'] || ep?.['ifl:subtitle'] || '',
  scripture_reference: ep?.scriptureReference || ep?.['ifl:scripture_reference'] || '',
  media_type:          ep?.mediaType || 'audio',      // 'audio' | 'video'
  duration_total:      ep?.durationSeconds || ep?.duration || 0,
});

// Derive pre/mid/post from IMA's AdPodInfo (ad.adPosition isn't on the native IMA ad object).
// getPodIndex() → 0=preroll, n>0=midroll #n, -1=postroll. If a caller passes an enriched
// object with a string adPosition, respect it.
const adPosition = (ad) => {
  if (typeof ad?.adPosition === 'string') return ad.adPosition;
  const idx = ad?.getAdPodInfo?.()?.getPodIndex?.();
  if (idx === -1) return 'post';
  if (idx > 0)    return 'mid';
  return 'pre';
};

// Derive 'audio' | 'video' for the ad itself. IMA ads expose getContentType() (MIME);
// the manual audio-ad path passes an enriched object with mediaType set.
const adMediaType = (ad) => {
  if (ad?.mediaType === 'audio' || ad?.mediaType === 'video') return ad.mediaType;
  const mime = ad?.getContentType?.() || '';
  return mime.startsWith('audio/') ? 'audio' : 'video';
};

const adFields = (ad) => ({
  ad_id:             ad?.getAdId?.() || ad?.adId || '',
  ad_title:          ad?.getTitle?.() || ad?.title || '',
  ad_position:       adPosition(ad),                  // 'pre' | 'mid' | 'post'
  ad_duration:       ad?.getDuration?.() || ad?.duration || 0,
  media_type:        adMediaType(ad),                 // 'audio' | 'video'
  click_through_url: ad?.getClickThroughUrl?.() || '',
});
